import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { makeStyles } from '@material-ui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert } from '@material-ui/lab';
import FileList from './FileList';

interface FileUploadProps {
  onChange: (files: File[]) => void;
  multiple?: boolean;
  invalidFiles?: File[];
  documents?: boolean;
  audio?: boolean;
  video?: boolean;
  images?: boolean;
  json?: boolean;
  disabled?: boolean;
  className?: string;
  // Used to identify the current upload batch.
  uploadId?: string;
}

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const useStyles = makeStyles((theme: Theme) => ({
  dropZone: {
    minWidth: 495,
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  listItem: {
    paddingLeft: 0,
    paddingBottom: 0,
  },
  listItemIcon: {
    minWidth: 24,
  },
}));

const FileUpload = (props: FileUploadProps) => {
  const classes = useStyles();
  const { onChange, invalidFiles, disabled, className, uploadId } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);

  const multiple = props.multiple !== undefined ? props.multiple : false;

  const handleRemoveFile = (index: number) => {
    files.splice(index, 1);
    setFiles([...files]);
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled) {
        return;
      }
      setRejectedFiles([]);
      setFiles([
        ...files,
        ...acceptedFiles.filter((file) => {
          for (let i = 0; i < files.length; ++i) {
            if (files[i].name === file.name && files[i].size === file.size) {
              return false;
            }
          }

          return true;
        }),
      ]);
    },
    [files, disabled],
  );

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    setRejectedFiles(fileRejections);
  }, []);

  let accept: Accept = {};

  if (props.documents) {
    accept = {
      ...accept,
      'application/msword': ['.doc', '.docx'],
      'application/octet-stream': ['.docx', '.pdf', '.pptx', '.xlsx'],
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.oasis.opendocument.text': ['.odt'],
      'application/vnd.oasis.opendocument.presentation': ['.odp'],
      'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        ['.ppsx', '.ppt', '.pptm', '.pptx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.doc', '.docx'],
      'application/wordperfect': ['.wpd'],
      'application/x-tex': ['.tex'],
      'application/zip': ['.zip'],
      'text/html': ['.htm', '.html'],
      'text/plain': ['.txt'],
      'text/rtf': ['.doc', '.rtf'],
    };
  }

  if (props.images) {
    accept = {
      ...accept,
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/gif': ['.gif'],
      'image/png': ['.png'],
      'image/tiff': ['.tif', '.tiff'],
    };
  }

  if (props.audio) {
    accept = {
      ...accept,
      'audio/mp4': ['.m4a'],
      'audio/mpeg': ['.mp3'],
    };
  }

  if (props.video) {
    accept = {
      ...accept,
      'video/avi': ['.avi'],
      'video/mp4': ['.mp4'],
      'video/mpeg': ['.mpeg'],
      'video/msvideo': ['.avi'],
      'video/quicktime': ['.mov'],
      'video/x-flv': ['.flv'],
      'video/x-msvideo': ['.avi'],
      'video/x-ms-wmv': ['.wmv'],
    };
  }

  if (props.json) {
    accept = {
      ...accept,
      'application/json': ['.json'],
    };
  }

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    onDropRejected,
    accept,
  });

  const style: any = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  useEffect(() => {
    setFiles([]);
    setRejectedFiles([]);
    setDirty(false);
  }, [uploadId]);

  useEffect(() => {
    let isDirty = dirty;

    if (files.length > 0) {
      isDirty = true;
      setDirty(true);
    }

    if (isDirty) {
      onChange(files);
    }
  }, [files]);

  return (
    <>
      {rejectedFiles.length > 0 && (
        <Alert severity="warning" className={classes.alert}>
          Onderstaande bestanden worden niet ondersteund:
          <List dense>
            {rejectedFiles.map((rejectedFile) => (
              <ListItem className={classes.listItem}>
                <ListItemIcon className={classes.listItemIcon}>
                  <FontAwesomeIcon icon={['fal', 'file']} />
                </ListItemIcon>
                <ListItemText>{rejectedFile.file.name}</ListItemText>
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      <Paper
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getRootProps({ style, multiple })}
        className={`${classes.dropZone} ${className || ''}`}
      >
        {!disabled && (
          <input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...getInputProps()}
          />
        )}
        {isDragActive && !isDragReject && (
          <Typography variant="body1">Sleep bestand hier naar toe</Typography>
        )}

        {isDragReject && (
          <Typography variant="body1">
            Dit type bestand wordt niet ondersteund.
          </Typography>
        )}

        {!isDragAccept && !isDragReject && (
          <Typography variant="body1">
            Sleep je bestanden hier, of klik om te selecteren.
          </Typography>
        )}
      </Paper>

      <FileList
        files={files}
        onRemove={handleRemoveFile}
        invalidFiles={invalidFiles}
      />
    </>
  );
};

export default FileUpload;
